//@flow
import {
    rider as riderApi,
    bulkRiderFetch as bulkRiderFetchApi,
    disableRider as disableRiderApi,
    bulkDisableRider as bulkDisableRiderApi,
    resetRiderPassword as resetRiderPasswordApi,
    bulkResetRiderPassword as bulkResetRiderPasswordApi,
    bulkResetRiderQrCode as bulkResetRiderQrCodeApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import {
    ToastTypes,
    CloseToastButton
} from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import type { MasterRider } from 'common/models/internal/MasterRider';
import type { Dispatch } from 'redux';
import { ENTITY_NAME_ID } from 'common/constants/entity/riderMaster';
import StringUtils from 'common/utils/StringUtils';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    riderId?: string
};

const ONE_MINUTE_IN_MS = 60000;

class RiderActions extends PaginatedEntityActions<MasterRider, Params> {
    superAddEntity = this.addEntity;

    entityNameId = ENTITY_NAME_ID;

    addEntity = (
        params: { clientId: string, riderId: string },
        entity: MasterRider,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (rider: MasterRider) => {
                        // authToken is null if enableAuth is false on rider persona
                        const password = rider.authToken?.token;
                        const toast = {
                            message: password
                                ? nsTranslate(
                                      'addRiderSuccessMessageWithPassword',
                                      {
                                          password
                                      }
                                  )
                                : nsTranslate('addRiderSuccessMessage'),
                            type: ToastTypes.Success,
                            timeout: ONE_MINUTE_IN_MS,
                            customAction: CloseToastButton
                        };
                        dispatch(showToast(toast, params));
                        resolve(rider);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                /* Since rider persona info is only presentational info derived from persona entity and not editable,
                it should not be included in the body  */
                const entityWithoutRiderPersona = entity.setRiderPersona(null);
                dispatch(
                    this.superAddEntity(
                        params,
                        entityWithoutRiderPersona,
                        options
                    )
                );
            });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, riderId: string },
        oldEntity: MasterRider,
        newEntity: MasterRider,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (rider: MasterRider) => {
                        const toast = {
                            message: nsTranslate('savedChangesMessage'),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(rider);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                /* Since rider persona info is only presentational and not editable,
                it should not be included for the patch diff */
                const oldEntityWithoutRiderPersona = oldEntity.setRiderPersona(
                    null
                );
                const newEntityWihoutRiderPersona = newEntity.setRiderPersona(
                    null
                );
                dispatch(
                    this.superPatchEntity(
                        params,
                        oldEntityWithoutRiderPersona,
                        newEntityWihoutRiderPersona,
                        options
                    )
                );
            });
        };
    };

    superDisableEntity = this.disableEntity;

    disableEntity = (
        params: { clientId: string, riderId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (rider: MasterRider) => {
                        const toast = {
                            message: nsTranslate('deleteEntitySuccessMessage', {
                                entityNameId: this.entityNameId
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(rider);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superDisableEntity(params, options));
            });
        };
    };

    showSuccessMessage = newRiderCount => {
        return (dispatch: Dispatch<*>) => {
            const toast = {
                message: StringUtils.toSentenceCaseFromMixedCase(
                    nsTranslate('bulkRiderImportSuccessMessage', {
                        count: newRiderCount
                    })
                ),
                type: ToastTypes.Success,
                timeout: ONE_MINUTE_IN_MS,
                customAction: CloseToastButton
            };
            dispatch(showToast(toast));
        };
    };
}

export default new RiderActions(
    'RIDER',
    riderApi,
    bulkRiderFetchApi,
    disableRiderApi,
    bulkDisableRiderApi,
    validateEntityIdsApi,
    null,
    null,
    resetRiderPasswordApi,
    bulkResetRiderPasswordApi,
    bulkResetRiderQrCodeApi
);
